import { Controller } from "@hotwired/stimulus"
import { SelectionToolbar } from "../selection_toolbar"

export default class SelectionToolbarController extends Controller {
  declare tableTarget: HTMLTableElement
  declare toolbarTarget: HTMLDivElement
  private selectionToolbars: Record<string, SelectionToolbar> = {}
  private selectedIdSet: Set<string> = new Set()
  private lastPage: number | null = null

  static targets = ["table", "toolbar"]

  static getInstance(element: Element) {
    return globalThis.Stimulus.getControllerForElementAndIdentifier(
      element,
      "selection-toolbar"
    ) as SelectionToolbarController | null
  }

  tableTargetConnected(table: HTMLTableElement) {
    // Clear selections if page changed (pagination)
    // Read page from the table's data attribute (set by server on each render)
    const currentPage = parseInt(table.dataset.page || "1", 10)
    if (this.lastPage !== null && this.lastPage !== currentPage) {
      this.selectedIdSet.clear()
    }
    this.lastPage = currentPage

    const hasSelections = this.selectedIdSet.size > 0
    const toolbar = new SelectionToolbar(
      table,
      this.toolbarTarget,
      hasSelections, // Skip initial clear if we have selections to restore
    )
    this.selectionToolbars[table.id] = toolbar

    // Restore selections after Turbo frame updates
    if (hasSelections) {
      toolbar.restoreSelections(this.selectedIdSet)
      // Prune selectedIdSet to only include currently visible IDs
      // This ensures filtered-out rows lose their selection (UX: "what you see is what you're working with")
      this.pruneSelectedIds(toolbar)
    }
  }

  private pruneSelectedIds(toolbar: SelectionToolbar) {
    const visibleIds = new Set(toolbar.getAllCheckboxIds())
    for (const id of this.selectedIdSet) {
      if (!visibleIds.has(id)) {
        this.selectedIdSet.delete(id)
      }
    }
  }

  selectAll(event: Event) {
    const toolbar = this.getSelectionToolbar(event)
    if (toolbar) {
      toolbar.selectAll(event)
      const isChecked = (event.target as HTMLInputElement)?.checked
      this.updateSelectedIds(toolbar.getAllCheckboxIds(), isChecked)
    }
  }

  handleChecked(event: Event) {
    const toolbar = this.getSelectionToolbar(event)
    if (toolbar) {
      toolbar.handleChecked()
      if (event.target instanceof HTMLInputElement) {
        this.updateSelectedIds([event.target.value], event.target.checked)
      }
    }
  }

  private updateSelectedIds(ids: string[], selected: boolean) {
    ids.forEach((id) => {
      if (selected) {
        this.selectedIdSet.add(id)
      } else {
        this.selectedIdSet.delete(id)
      }
    })
  }

  selectedIds(): string[] {
    return Array.from(this.selectedIdSet)
  }

  clearSelected() {
    this.selectedIdSet.clear()
    Object.values(this.selectionToolbars).forEach((toolbar) => {
      toolbar.clearSelected()
    })
  }

  getSelectionToolbar(event: Event) {
    if (event.target instanceof HTMLInputElement) {
      const table = event.target.closest("table")
      if (table) {
        return this.selectionToolbars[table.id]
      } else {
        console.log("Missing table for selection toolbar")
      }
    }
  }
}
